import { useState } from "react";
import { ChevronDown, History, Loader2, Undo2 } from "lucide-react";

const ASSET_LABELS = {
  positioning: "Positioning", personas: "Personas",
  linkedin: "LinkedIn", x: "X", producthunt: "Product Hunt", ads: "Ads",
  email: "Email", "email.body": "Email body", "email.subject_lines": "Subject lines", "email.cta_variants": "CTA variants",
  landing: "Landing", "landing.headline": "Landing headline", "landing.subheadline": "Landing subheadline", "landing.bullets": "Landing bullets",
  seo: "SEO brief", outreach: "Outreach",
};

const plain = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
};

export const HistoryTab = ({ campaign, onRestore, restoring }) => {
  const [open, setOpen] = useState(null);
  const history = campaign.history || [];

  if (!history.length) {
    return (
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-8 text-center" data-testid="tab-panel-history">
        <History className="w-6 h-6 text-zinc-500 mx-auto mb-4" strokeWidth={1.5} />
        <p className="text-sm text-zinc-400" data-testid="history-empty">
          No refinements yet. Every AI refinement you apply is versioned here — restore any earlier take with one click.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="tab-panel-history">
      {[...history].reverse().map((entry, ri) => {
        const index = history.length - 1 - ri;
        const isOpen = open === index;
        return (
          <div key={`${entry.at}-${index}`} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-5" data-testid={`history-entry-${index}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-400">
                    v{index + 1}{entry.restored ? " · restored" : ""}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                    {ASSET_LABELS[entry.asset] || entry.asset}
                  </span>
                </div>
                <p className="text-sm text-white truncate" data-testid={`history-feedback-${index}`}>&ldquo;{entry.feedback}&rdquo;</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-1">
                  {new Date(entry.at).toLocaleString()} · {entry.by}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={() => setOpen(isOpen ? null : index)} data-testid={`history-view-${index}`}
                  className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white border border-white/10 rounded-full px-3 py-1.5 transition-colors duration-300">
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} strokeWidth={1.5} />
                  View
                </button>
                <button type="button" onClick={() => onRestore(index)} disabled={restoring !== null} data-testid={`history-restore-${index}`}
                  className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white border border-white/10 rounded-full px-3 py-1.5 disabled:opacity-40 transition-colors duration-300">
                  {restoring === index ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : <Undo2 className="w-3.5 h-3.5" strokeWidth={1.5} />}
                  Restore
                </button>
              </div>
            </div>
            {isOpen && (
              <p className="mt-4 pt-4 border-t border-white/10 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap" data-testid={`history-content-${index}`}>
                {plain(entry.after)}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};
